import { X } from "lucide-react"

import LunarEnergyImage from "@/assets/img/modal/popover_image_lunar_energy.webp"
import YellowPalm from "@/assets/img/modal/yellow_left_palm.svg?react"
import { useCreditsPurchase } from "@/features/advisors"
import { specialOfferPlan, useChat } from "@/features/chat"

import type { AppModalComponentProps } from "../config/modal-registry"

/**
 * Stars pack promo v2 (Figma: Popovers_Stars_Special_Offer, 3153:52018).
 * Charges `specialOfferPlan` via the stored subscription payment method.
 */
export function StarsSpecialOfferV2Modal({ onClose }: AppModalComponentProps) {
  const { userMainSubscriptionId } = useChat()
  const { purchase, isLoading, error } = useCreditsPurchase({ userMainSubscriptionId })

  const handleContinue = () => {
    void purchase(specialOfferPlan, onClose)
  }

  return (
    <div
      data-slot="stars-special-offer-v2-modal"
      className="fixed inset-0 z-[1200] mx-auto flex min-h-[100dvh] w-full max-w-app flex-col justify-end bg-dark-base/80 backdrop-blur-[10px]"
    >
      {error ? (
        <div className="absolute left-1/2 top-6 z-[10] w-[calc(100%-32px)] max-w-app -translate-x-1/2 rounded-xl bg-peach px-4 py-3 text-center font-body text-sm text-white">
          {error}
        </div>
      ) : null}

      <div className="relative flex w-full flex-col items-end overflow-hidden rounded-t-3xl bg-dark-surface p-4">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          disabled={isLoading}
          className="relative z-10 flex size-6 shrink-0 items-center justify-center text-purple-light disabled:opacity-50"
        >
          <X className="size-5" strokeWidth={2} />
        </button>

        <div className="flex w-full flex-col items-center">
          <div className="relative mb-[-24px] flex w-full flex-col items-center">
            <div className="relative z-[2] mb-[-24px] flex w-full flex-col items-center gap-2 text-center">
              <h2 className="w-full font-display text-2xl font-medium text-purple-light">
                Lunar Energy Is Active
              </h2>
              <p className="w-full font-body text-base text-purple-light/70">
                The Moon may bring quiet questions to the surface. Extra advisor time is available at
                a reduced price.
              </p>
            </div>
            <img
              src={LunarEnergyImage}
              alt=""
              className="pointer-events-none relative z-0 h-[315px] w-[538px] max-w-none object-cover"
            />
          </div>

          <div className="relative z-[2] flex w-full flex-col gap-4 bg-dark-surface">
            <div className="flex w-full flex-col items-center gap-2">
              <div className="flex items-start justify-center gap-2">
                <div className="flex h-[30px] items-center rounded-[15px] bg-green/20 px-3 py-2">
                  <p className="font-body text-[10px] font-medium uppercase text-green">
                    LIMITED OFFER
                  </p>
                </div>
                <div className="flex items-center justify-center gap-1 rounded-2xl bg-gold/20 p-2">
                  <YellowPalm className="h-3.5 w-1.5 shrink-0" aria-hidden />
                  <p className="font-body text-[10px] font-medium text-gold">EXCLUSIVE</p>
                  <YellowPalm className="h-3.5 w-1.5 shrink-0 -scale-x-100" aria-hidden />
                </div>
              </div>

              <div className="flex w-full flex-col items-center">
                <h3 className="w-full text-center font-display text-2xl font-medium text-purple-light">
                  Get {specialOfferPlan.creditsCount} stars just for
                </h3>
                <div className="flex items-center gap-2">
                  <span className="relative inline-grid">
                    <span className="font-display text-2xl font-medium text-purple-light/40">
                      ${specialOfferPlan.unitAmountNoDiscount}
                    </span>
                    <span
                      aria-hidden
                      className="absolute left-0.5 top-1/2 h-0.5 w-full -translate-y-1/2 bg-peach"
                    />
                  </span>
                  <span className="font-display text-2xl font-medium text-purple-light">
                    ${specialOfferPlan.unitAmount}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex w-full flex-col items-center gap-2">
              <button
                type="button"
                onClick={handleContinue}
                disabled={isLoading}
                className="flex h-14 w-full items-center justify-center gap-2 rounded-[80px] bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white disabled:bg-none disabled:bg-purple-light/10 disabled:text-purple-light/70"
              >
                {isLoading ? (
                  <>
                    <span className="size-5 animate-spin rounded-full border-2 border-purple-light border-t-transparent" />
                    Purchasing...
                  </>
                ) : (
                  "Continue"
                )}
              </button>
              <p className="font-body text-sm text-purple-light/40">
                One-time payment is non-refundable
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
